const BASE_URL = process.env.REACT_APP_BASE_URL;
const AUTH_KEY = "member-portal-auth";
const USER_INFO_KEY = "user-info";

function getStoredAuth() {
  try {
    const raw = sessionStorage.getItem(AUTH_KEY) || localStorage.getItem(AUTH_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveMemberAuth(payload) {
  const auth = {
    accessToken: payload.accessToken,
    refreshToken: payload.refreshToken,
    expiresAt: payload.expiresAt,
    user: payload.user,
    member: payload.member,
  };
  sessionStorage.setItem(AUTH_KEY, JSON.stringify(auth));
  localStorage.setItem(AUTH_KEY, JSON.stringify(auth));

  // Main layout compatible session so existing guard keeps working
  const userInfo = { user: payload.user };
  sessionStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
  localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
  return auth;
}

export function clearMemberAuth() {
  sessionStorage.removeItem(AUTH_KEY);
  localStorage.removeItem(AUTH_KEY);
  sessionStorage.removeItem(USER_INFO_KEY);
  localStorage.removeItem(USER_INFO_KEY);
  sessionStorage.removeItem("tab-index");
}

export function getMemberAuth() {
  return getStoredAuth();
}

export function isMemberSession() {
  const auth = getStoredAuth();
  if (!auth?.accessToken || !auth?.user) return false;
  const role = String(auth.user.role || "").toLowerCase();
  return role === "user" || role === "member";
}

async function refreshAccessToken() {
  const auth = getStoredAuth();
  if (!auth?.refreshToken) {
    throw new Error("No refresh token");
  }

  const response = await fetch(`${BASE_URL}member/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: auth.refreshToken }),
  });

  const body = await response.json().catch(() => ({}));
  if (!response.ok || !body.success) {
    clearMemberAuth();
    throw new Error(body?.error?.message || "Session expired");
  }

  const next = {
    ...auth,
    accessToken: body.data.accessToken,
    refreshToken: body.data.refreshToken,
    expiresAt: body.data.expiresAt,
  };
  sessionStorage.setItem(AUTH_KEY, JSON.stringify(next));
  localStorage.setItem(AUTH_KEY, JSON.stringify(next));
  return next;
}

/**
 * Authenticated fetch for member portal APIs.
 * Transparently refreshes on 401 and redirects to /login when refresh fails.
 */
export async function memberPortalFetch(path, options = {}) {
  const { skipAuth = false, _retried = false, ...rest } = options;
  const headers = {
    "Content-Type": "application/json",
    ...(rest.headers || {}),
  };

  if (!skipAuth) {
    const auth = getStoredAuth();
    if (auth?.accessToken) {
      headers.Authorization = `Bearer ${auth.accessToken}`;
    }
  }

  const response = await fetch(`${BASE_URL}${path.replace(/^\//, "")}`, {
    ...rest,
    headers,
  });

  if (response.status === 401 && !skipAuth && !_retried) {
    try {
      await refreshAccessToken();
      return memberPortalFetch(path, { ...options, _retried: true });
    } catch {
      clearMemberAuth();
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
      throw new Error("Session expired");
    }
  }

  const body = await response.json().catch(() => ({}));
  return { response, body };
}

export async function memberLoginRequest(identifier, password) {
  const { response, body } = await memberPortalFetch("member/auth/login", {
    method: "POST",
    skipAuth: true,
    body: JSON.stringify({ identifier, password }),
  });
  return { ok: response.ok && body.success, status: response.status, body };
}

export async function memberLogoutRequest() {
  try {
    await memberPortalFetch("member/auth/logout", { method: "POST" });
  } catch {
    // ignore
  } finally {
    clearMemberAuth();
  }
}

export async function getMemberDashboard() {
  const { response, body } = await memberPortalFetch("member/dashboard");
  return { ok: response.ok && body.success, body };
}

export async function getMemberProfile() {
  const { response, body } = await memberPortalFetch("member/profile");
  return { ok: response.ok && body.success, body };
}

export async function getMemberInvoices({ status = "all", page = 1, limit = 10 } = {}) {
  const qs = new URLSearchParams({ status, page: String(page), limit: String(limit) });
  const { response, body } = await memberPortalFetch(`member/invoices?${qs.toString()}`);
  return { ok: response.ok && body.success, body };
}

export async function getMemberInvoiceDetail(invoiceId) {
  const { response, body } = await memberPortalFetch(
    `member/invoices/${invoiceId}/detail`
  );
  return { ok: response.ok && body.success, body };
}

export async function initiateMemberInvoicePayment(invoiceId) {
  const { response, body } = await memberPortalFetch(
    `member/invoices/${invoiceId}/pay`,
    { method: "POST" }
  );
  return { ok: response.ok && body.success, status: response.status, body };
}

export async function getMemberPaymentSessionStatus(sessionId) {
  const { response, body } = await memberPortalFetch(
    `member/payments/${sessionId}`
  );
  return { ok: response.ok && body.success, body };
}

export async function adminResetMemberPassword(memberId, actingUserId, newPassword) {
  const { response, body } = await memberPortalFetch(
    `member-credentials/${memberId}/password`,
    {
      method: "PUT",
      skipAuth: true,
      body: JSON.stringify({ actingUserId, newPassword }),
    }
  );
  return { ok: response.ok && body.success, body };
}

export async function getMemberCredentialStatus(memberId, actingUserId) {
  const qs = new URLSearchParams({ actingUserId: String(actingUserId) });
  const { response, body } = await memberPortalFetch(
    `member-credentials/${memberId}?${qs.toString()}`,
    { skipAuth: true }
  );
  return { ok: response.ok && body.success, body };
}
